// src/pages/healthcare/consultation/VideoConsultation.tsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button, message, notification, Space, Typography, Tooltip, Badge, Popover, Popconfirm } from "antd";
import { UserOutlined, TeamOutlined, VideoCameraOutlined, DesktopOutlined, SyncOutlined, ExpandOutlined, CompressOutlined, ExportOutlined, AudioMutedOutlined } from "@ant-design/icons";
import { getSessionUser } from "../../../utils/auth/sessionUser";

// API Imports
import { fetchMedicalHistories, fetchPatient, updatePatient } from "../../../api/healthcare/PatientApi";
import { fetchSingleCompletedAppointmentById } from "../../../api/healthcare/Appointment/AppointmentApi";
import { fetchTests } from "../../../api/healthcare/Testing/TestApi";
import { fetchMedications } from "../../../api/healthcare/MedicationsApi";
import { fetchClinicCompany } from "../../../api/admin/companyApi";
import { Company } from "../../../types/admin/CompanyTypes";

import { uploadPractitionerChatAttachment } from "../../../api/healthcare/Consultation/ConsultChatApi";
import { Patient } from "../../../types/healthcare/PatientTypes";
import { Appointment, CompletedAppointment, defaultConsult } from "../../../types/appointments/AppointmentTypes";
import { PatientNote } from "../../../types/healthcare/PatientNotes";
import { consultParticipant, SpeakerMap, VideoParticipant } from "../../../types/appointments/ConsultTypes";
import { TeamMember } from "../../../types/admin/TeamTypes";

// Services
import SocketService from "../../../services/socketService";

// Hooks
import { useConsultTranscripts, useTranscriberStatus, safeParseTranscripts, transcriberStatusFromError, transcriberStatusFromEvent } from "../../../hooks/consult/useConsultTranscripts";
import { useTranscriberCapture } from "../../../hooks/consult/useTranscriberCapture";
import { useFinalTranscript } from "../../../hooks/consult/useFinalTranscript";
import { useConsultSuggestions } from "../../../hooks/consult/useConsultSuggestions";
import { useConsultChat } from "../../../hooks/consult/useConsultChat";
import { useDebounce } from "../../../hooks/consult/useDebounce";
import { useActiveTreatmentPlan } from "../../../hooks/useActiveTreatmentPlan";
import { useConsultSession } from "../../../hooks/consult/useConsultSession";
import { useXoeFocusPublisher } from "../../../context/XoeFocusContext";
import { useEndConsult } from "../../../hooks/consult/useEndConsult";
import { usePatientAutoSave } from "../../../hooks/consult/usePatientAutoSave";
import { useConsultMediaTelemetry, useRemoteVideoRegistry } from "../../../hooks/consult/useConsultMediaTelemetry";
import { useCaptureTelemetry } from "../../../hooks/consult/useCaptureTelemetry";

// Utils
import { Suggestion } from "../../../utils/healthcare/SugestionUtils";
import { buildVideoDisplayNames } from "../../../utils/consult/participantIdentity";
import { emptySpeakerMap, parseSpeakerMap } from "../../../utils/consult/speakerNames";
import { createPeerMesh, PeerMesh } from "../../../utils/consult/peerMesh";
import { getPatientDataSnapshot } from "../../../utils/consult/patientSnapshot";
import { acquireReplacement, getUserMediaWithTimeout, isGetUserMediaTimeout, listInputDevices, MediaKind, releaseReplaced, Replacement, seedDeviceSelection, swapOntoPeers, watchDeviceList, watchTrackEnded } from "../../../utils/consult/localMedia";
import { formatInZone } from "../../../utils/time";

// Components
import EditPatientModal from "../patient/PatientModal";
import type { TranscriptListProps } from "./consultationComponents/Transcript";
import EndConsultModal from "./consultationComponents/EndConsultModal";
import AdvancedSettingsDialog from "./consultationComponents/AdvancedSettingsDialog";
import ConsultDrawerShell from "./consultationComponents/ConsultDrawerShell";
import ConsultDrawerTitle from "./consultationComponents/ConsultDrawerTitle";
import ConsultContentTabs from "./consultationComponents/ConsultContentTabs";
import ConsultRightPanel, { RightPanelMode } from "./consultationComponents/ConsultRightPanel";
import ConsultTranscriptDrawer from "./consultationComponents/ConsultTranscriptDrawer";
import type { UserUuid } from "../../../types/identity";

const { Text } = Typography;


const CAMERA_CONSTRAINTS: MediaTrackConstraints = { width: { min: 640, ideal: 1280, max: 1920 }, height: { min: 480, ideal: 720, max: 1080 }, frameRate: { ideal: 30, max: 30 }, aspectRatio: { ideal: 16 / 9 } };
// The Advanced Settings toggles never reach the call's microphone, only the transcription capture (D5).
const MIC_CONSTRAINTS: MediaTrackConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

const withoutKey = <V,>(map: Map<string, V>, key: string): Map<string, V> => {
	if (!map.has(key)) return map;
	const next = new Map(map);
	next.delete(key);
	return next;
};

// ─── Standalone component: a single remote participant's video tile ───────────
interface RemoteVideoTileProps {
	socketId: string;
	stream: MediaStream;
	label?: string;
	isScreenShare?: boolean;
	micSilent?: boolean;
	registerVideo?: (socketId: string, element: HTMLVideoElement | null) => void;
}

const RemoteVideoTile: React.FC<RemoteVideoTileProps> = ({ socketId, stream, label, isScreenShare, micSilent, registerVideo }) => {
	const ref = React.useRef<HTMLVideoElement | null>(null);
	const setVideo = useCallback(
		(element: HTMLVideoElement | null) => {
			ref.current = element;
			registerVideo?.(socketId, element);
		},
		[socketId, registerVideo],
	);
	React.useEffect(() => {
		if (ref.current && stream) {
			ref.current.srcObject = stream;
			ref.current.play().catch(() => { });
		}
	}, [stream]);
	return (
		<div style={{ position: "relative", background: "#111", borderRadius: 6, overflow: "hidden", height: "100%", minHeight: 100 }}>
			<video ref={setVideo} autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: isScreenShare ? "contain" : "cover", display: "block" }} />
			{/* Inline styles only: antd's CSS is never injected into the pop-out window. */}
			{micSilent && (
				<div data-testid="consultation-peer-mic-silent" role="status" style={{ position: "absolute", bottom: 30, left: 8, display: "flex", alignItems: "center", gap: 6, padding: "2px 8px", background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 11, borderRadius: 4, userSelect: "none" }}>
					<AudioMutedOutlined style={{ color: "#faad14" }} />
					{"Patient's microphone appears silent"}
				</div>
			)}
			{label && <div style={{ position: "absolute", bottom: 6, left: 8, padding: "2px 8px", background: "rgba(0,0,0,0.6)", fontSize: 11, borderRadius: 4, userSelect: "none" }}>{label}</div>}
		</div>
	);
};
// ─────────────────────────────────────────────────────────────────────────────

type WaitingPatient = { socketId: string; userId?: string; displayName: string; joinedAt: number };

// Covers a talk that predates removeParticipant, or one restarted mid-click: neither ever acks.
const REMOVE_ACK_TIMEOUT_MS = 10_000;
// Never talk's own error text: it is one refusal for every target, by design.
const REMOVE_FAILED_MESSAGE = "Could not remove the participant. Please try again.";
const DISMISS_REASON = "The practitioner is not available at this time.";

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

// The join time appears only where it tells two rows apart, e.g. one patient on a laptop and a phone.
const rowLabels = (rows: { label: string; joinedAt?: number }[]): string[] =>
	rows.map(({ label, joinedAt }) => (joinedAt && rows.filter((row) => row.label === label).length > 1 ? `${label} · joined ${formatInZone(joinedAt, "HH:mm")}` : label));

interface ParticipantListPanelProps {
	videoParticipants: VideoParticipant[];
	waitingPatients: WaitingPatient[];
	mySocketId: string;
	consultationStarted: boolean;
	pendingRemovals: ReadonlySet<string>;
	onAdmit: (socketId: string) => void;
	onDismiss: (socketId: string) => void;
	onRemove: (socketId: string) => void;
}

// Module scope, not the render body: a component defined in render remounts each render and closes an open Popconfirm.
const ParticipantListPanel = React.memo(function ParticipantListPanel({ videoParticipants, waitingPatients, mySocketId, consultationStarted, pendingRemovals, onAdmit, onDismiss, onRemove }: ParticipantListPanelProps) {
	const hasPatient = videoParticipants.some((p) => p.participantType.toLowerCase() === "patient");
	const waitingLabels = rowLabels(waitingPatients.map((wp) => ({ label: wp.displayName || "Patient", joinedAt: wp.joinedAt })));
	const participantLabels = rowLabels(videoParticipants.map((p) => ({ label: p.displayName || capitalise(p.participantType), joinedAt: p.joinedAt })));
	return (
		<div style={{ padding: 12, background: "var(--background-color)", borderRadius: 8, marginBottom: 8 }}>
			<Space direction="vertical" style={{ width: "100%" }} size="small">
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
					<Text strong style={{ fontSize: 13 }}>
						Participants
					</Text>
					<Badge count={videoParticipants.length} style={{ backgroundColor: hasPatient ? "#52c41a" : "#faad14" }} />
				</div>

				{videoParticipants.length === 0 && waitingPatients.length === 0 && (
					<Text type="secondary" style={{ fontSize: 12 }}>
						Waiting for participants to join...
					</Text>
				)}

				{/* Waiting room section — admit patients before they join the video */}
				{waitingPatients.length > 0 && (
					<div style={{ marginBottom: 8 }}>
						<Text strong style={{ fontSize: 12, color: "#faad14" }}>
							Waiting Room ({waitingPatients.length})
						</Text>
						{waitingPatients.map((wp, index) => (
							<div
								key={wp.socketId}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									padding: "8px 12px",
									background: "rgba(250,173,20,0.08)",
									borderRadius: 6,
									border: "1px solid #faad14",
									marginTop: 4,
								}}
							>
								<Space size="small">
									<UserOutlined style={{ color: "#faad14" }} />
									<Text style={{ fontSize: 12 }}>{waitingLabels[index]}</Text>
								</Space>
								<Space size={4}>
									<Button size="small" data-testid="consultation-waiting-dismiss" onClick={() => onDismiss(wp.socketId)}>
										Dismiss
									</Button>
									<Button size="small" type="primary" data-testid="consultation-waiting-admit" onClick={() => onAdmit(wp.socketId)}>
										Admit
									</Button>
								</Space>
							</div>
						))}
					</div>
				)}

				{videoParticipants.map((p, index) => {
					const isMe = p.socketId === mySocketId;
					return (
						<div
							key={p.socketId}
							data-testid="consultation-participant-row"
							data-socket-id={p.socketId}
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								padding: "8px 12px",
								background: "var(--card-background)",
								borderRadius: 6,
								border: "1px solid var(--border-color)",
							}}
						>
							<Space size="small">
								<UserOutlined />
								<Text style={{ fontSize: 12 }}>{isMe ? `${participantLabels[index]} (you)` : participantLabels[index]}</Text>
							</Space>
							<Space size={4}>
								{p.ready && <Badge status="success" text={<Text style={{ fontSize: 11, color: "#52c41a" }}>Ready</Text>} />}
								{p.videoEnabled && <VideoCameraOutlined style={{ fontSize: 12, color: "#52c41a" }} />}
								{p.participantType === "patient" && !isMe && (
									<Popconfirm
										title="Remove this participant?"
										description="They will need to be admitted again to rejoin."
										okText="Remove"
										okType="danger"
										okButtonProps={{ "data-testid": "consultation-participant-remove-confirm" } as never}
										onConfirm={() => onRemove(p.socketId)}
									>
										<Button size="small" danger data-testid="consultation-participant-remove" disabled={pendingRemovals.has(p.socketId)}>
											Remove
										</Button>
									</Popconfirm>
								)}
							</Space>
						</div>
					);
				})}

				{!hasPatient && videoParticipants.length > 0 && (
					<div style={{ marginTop: 8, padding: "8px 12px", background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 6 }}>
						<Text style={{ fontSize: 11, color: "#d46b08" }}>⚠️ Patient has not joined yet</Text>
					</div>
				)}
				{hasPatient && !consultationStarted && (
					<div style={{ marginTop: 8, padding: "8px 12px", background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 6 }}>
						<Text style={{ fontSize: 11, color: "#52c41a" }}>✓ Patient ready. Start the consultation.</Text>
					</div>
				)}
			</Space>
		</div>
	);
});

interface VideoConsultationProps {
	roomId: string;
	isModalOpen: boolean;
	handleCloseConsultationModal: () => void;
	patientId: UserUuid;
	appointment: Appointment;
}

const VideoConsultation: React.FC<VideoConsultationProps> = ({ roomId, isModalOpen, handleCloseConsultationModal, patientId, appointment }) => {
	// Patient & Consultation State
	const [patient, setPatient] = useState<Patient | null>(null);
	const [company, setCompany] = useState<Company | null>(null);
	const [practitioner, setPractitioner] = useState<TeamMember | null>(null);
	const [patientNotes, setPatientNotes] = useState<PatientNote[]>([]);
	const [consultation, setConsultation] = useState<CompletedAppointment>(defaultConsult);

	// Transcripts & Suggestions. The feed owns dedupe (by server id), ordering (oldest first) and
	// every parse of a stored/broadcast value — see hooks/useConsultTranscripts.
	const { transcripts, upsert: upsertTranscript, upsertMany: upsertTranscripts, setAll: setAllTranscripts } = useConsultTranscripts();
	const { status: transcriberStatus, report: reportTranscriberStatus } = useTranscriberStatus();
	const [transcriptDrawerOpen, setTranscriptDrawerOpen] = useState<boolean>(false);
	const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
	const [participants, setParticipants] = useState<consultParticipant[]>([]);
	const [videoParticipants, setVideoParticipants] = useState<VideoParticipant[]>([]);
	// audioParticipantId → display name for the transcript view. Both fields already round-trip
	// through participantJoined/videoRoomState, so the roster in state is the whole source; our
	// own entry is seeded separately because the server need not echo us back in every payload.
	const displayNames = useMemo(
		() =>
			buildVideoDisplayNames(videoParticipants, {
				audioParticipantId: `practitioner_${practitioner?.id || "unknown"}`,
				displayName: practitioner?.first_name ? `${practitioner.first_name} ${practitioner.last_name || ""}`.trim() : "Practitioner",
			}),
		[videoParticipants, practitioner],
	);

	// Speaker label → who that is. Read-only here: online segments carry `speakerLabel ===
	// participantId`, so the roster above already names every microphone and there is nothing for
	// a practitioner to assign. The map is still consumed, because a room can be joined from both
	// consult kinds and a name set elsewhere must not vanish on this screen.
	const [speakerMap, setSpeakerMap] = useState<SpeakerMap>(emptySpeakerMap);

	// The final transcription pass lands minutes after the consult is saved — usually while the
	// practitioner is already reading the record — so the completed view upgrades itself in place.
	// It replaces the segments outright: the stored column IS the record here, there is no live
	// feed to union with, and the whole point of the pass is that a segment can be corrected.
	//
	// The speaker map is deliberately left alone — the final pass re-uses the live labels rather
	// than renumbering, and its save carries no `speaker_map`. See InPersonConsultation.
	const applyFinalTranscript = useCallback(
		(upgraded: CompletedAppointment) => {
			setAllTranscripts(upgraded.transcript);
			setConsultation(upgraded);
		},
		[setAllTranscripts],
	);

	useFinalTranscript({
		appointmentId: appointment.id,
		status: consultation.transcript_status,
		enabled: appointment.status === "Completed",
		onFinal: applyFinalTranscript,
	});

	// Consult Guidance
	const [rightPanelMode, setRightPanelMode] = useState<RightPanelMode>("notes");
	const { consultSessionId } = useConsultSession({ appointmentId: appointment.id, enabled: appointment.status !== "Completed" });

	// S13 — a LIVE consult on the Xoe focus bus (presence `in_consult`); a completed one under review is not.
	const { setConsultFocus, clearConsultFocus } = useXoeFocusPublisher();
	useEffect(() => {
		if (appointment.status === "Completed") return;
		setConsultFocus(true);
		return () => clearConsultFocus();
	}, [appointment.status, setConsultFocus, clearConsultFocus]);

	// Active treatment plan (loaded on mount for consult mode)
	const { activePlan, setActivePlan, reloadPlan, stageMatchesAppointment } = useActiveTreatmentPlan({ patientId, appointmentTypeId: appointment.appointment_type_id, enabled: appointment.status !== "Completed" });
	// Tracks whether we auto-switched to plan_notes so session load doesn't override it
	const planNotesActiveRef = useRef(false);
	useEffect(() => {
		if (!stageMatchesAppointment) return;
		planNotesActiveRef.current = true;
		setRightPanelMode("plan_notes");
	}, [stageMatchesAppointment]);
	useEffect(() => {
		if (consultSessionId && !planNotesActiveRef.current) setRightPanelMode("notes");
	}, [consultSessionId]);

	// Waiting room — patients waiting to be admitted
	const [waitingPatients, setWaitingPatients] = useState<WaitingPatient[]>([]);

	// Remove clicks awaiting talk's ack, keyed by the target socket id.
	const [pendingRemovals, setPendingRemovals] = useState<ReadonlySet<string>>(() => new Set());
	const removalTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
	// No argument settles every pending removal: videoError names no target. Returns whether anything was pending.
	const settleRemoval = useCallback((socketId?: string): boolean => {
		const timers = removalTimersRef.current;
		const ids = socketId === undefined ? Array.from(timers.keys()) : timers.has(socketId) ? [socketId] : [];
		if (ids.length === 0) return false;
		ids.forEach((id) => {
			clearTimeout(timers.get(id));
			timers.delete(id);
		});
		setPendingRemovals(new Set(timers.keys()));
		return true;
	}, []);

	// AI
	const [aiActive, setAiActive] = useState<boolean>(false);
	const [aiEnabled, setAiEnabled] = useState<boolean>(false);

	// Video State
	const [isScreenSharing, setIsScreenSharing] = useState<boolean>(false);
	const [videoExpanded, setVideoExpanded] = useState<boolean>(false);
	const [reconnecting, setReconnecting] = useState<boolean>(false);
	const [connectionState, setConnectionState] = useState<"new" | "connecting" | "connected" | "disconnected" | "failed" | "closed">("new");

	// Modals
	const [participantsPopoverOpen, setParticipantsPopoverOpen] = useState<boolean>(false);
	const [isPatientModalOpen, setIsPatientModalOpen] = useState<boolean>(false);
	const [isEndModalOpen, setIsEndModalOpen] = useState<boolean>(false);
	const [advancedSettingsVisible, setAdvancedSettingsVisible] = useState<boolean>(false);

	// Device Settings
	const [microphones, setMicrophones] = useState<any[]>([]);
	const [cameras, setCameras] = useState<any[]>([]);
	const [selectedCamera, setSelectedCamera] = useState<string>("");

	// Multi-participant remote streams (socketId → MediaStream)
	const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map());
	const [silentMicPeers, setSilentMicPeers] = useState<Map<string, boolean>>(new Map());
	// True once getUserMedia has resolved and localStreamRef is populated
	const [localStreamReady, setLocalStreamReady] = useState<boolean>(false);
	// Video pop-out state
	const [videoPopped, setVideoPopped] = useState<boolean>(false);
	const [pipContainer, setPipContainer] = useState<Element | null>(null);
	const pipWindowRef = useRef<Window | null>(null);
	const pipIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// Refs
	const socketServiceRef = useRef<SocketService | null>(null);
	const localVideoRef = useRef<HTMLVideoElement>(null);
	// Callback ref: fires synchronously when the <video> element mounts/re-mounts
	// (e.g. when the Drawer first renders its content). Immediately attaches the
	// stream so the practitioner's own video is visible without waiting for a
	// useEffect dependency to change.
	const localVideoCallbackRef = useCallback((node: HTMLVideoElement | null) => {
		(localVideoRef as React.MutableRefObject<HTMLVideoElement | null>).current = node;
		if (node && localStreamRef.current) {
			node.srcObject = localStreamRef.current;
			node.play().catch(() => { });
		}
	}, []);
	// The per-peer WebRTC state machine — one negotiation core shared with the patient page
	// (utils/consult/peerMesh): perfect-negotiation glare handling, candidate queueing, ICE-restart
	// renegotiation and the failed-connection watchdog all live there, created per room in init.
	const meshRef = useRef<PeerMesh | null>(null);
	const localStreamRef = useRef<MediaStream | null>(null);
	const screenStreamRef = useRef<MediaStream | null>(null);
	const isScreenSharingRef = useRef<boolean>(false);
	const mySocketIdRef = useRef<string>("");
	const switchingDevicesRef = useRef<boolean>(false);
	const unwatchLocalTracksRef = useRef<() => void>(() => {});
	const recoveringDeviceRef = useRef<boolean>(false);
	const recheckLostDeviceRef = useRef<boolean>(false);

	// Live consult chat, on the same room socket as the transcript feed. Mounted here rather than
	// beside useConsultTranscripts because it needs socketServiceRef, declared just above.
	// ⚠️ `chatMessages` must stay in THIS scope — the end-of-consult save archives it (S7).
	const {
		messages: chatMessages,
		sendMessage: sendChatMessage,
		sendAttachment: sendChatAttachment,
		unreadCount: chatUnreadCount,
		markRead: markChatRead,
		error: chatError,
		dismissError: dismissChatError,
	} = useConsultChat({
		roomId,
		getSocket: () => (socketServiceRef.current?.getSocket() as any) ?? null,
		// A completed consult has no socket at all — the archive is AppointmentDrawer's ArchivedChatView.
		enabled: appointment.status !== "Completed",
		senderName: practitioner?.first_name ? `${practitioner.first_name} ${practitioner.last_name || ""}`.trim() : undefined,
		selfRole: "practitioner",
		// healthcare's practitioner route takes `app_id` and does Appointment::find() on it.
		uploadAttachment: (file) => uploadPractitionerChatAttachment(appointment.id, file),
	});

	// Depends on the message count too, so arrivals while the panel is already open stay read.
	useEffect(() => {
		if (rightPanelMode === "chat") markChatRead();
	}, [rightPanelMode, chatMessages.length, markChatRead]);

	// Deferred reconnection handling
	const pendingReconnectRef = useRef<boolean>(false);
	const mediaReadyRef = useRef<boolean>(false);

	const patientLoadedRef = useRef<boolean>(false);
	const patientDataSentRef = useRef<boolean>(false);
	const roomDataLoadedRef = useRef<boolean>(false);
	const roomDataRequestedRef = useRef<boolean>(false);
	const previousPatientDataRef = useRef<string>("");
	const patientUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Stable ref so socket listeners (registered once in init) can always call the latest startConsultation
	const startConsultationFnRef = useRef<() => void>(() => {});

	const debouncedPatient = useDebounce(patient, 1000);
	const { handleAutoSave } = usePatientAutoSave({ patient, roomId });

	// Read once at mount — `practitioner` is only set inside init(), and a changing id would churn the hook's callbacks and the socket init effect behind them.
	const [captureParticipantId] = useState<string>(() => {
		const user = getSessionUser();
		return `practitioner_${user?.id || "unknown"}`;
	});
	// Declared above every consumer: a useCallback dep read before its declaration white-screens the drawer.
	const {
		consultationStarted,
		consultationStartedRef,
		markConsultationStarted,
		ensureTranscriberRunning,
		startCapture,
		stopCapture,
		toggleTranscribeListener,
		transcriberEnabled,
		transcriberActive,
		captureState,
		audioStreamingServiceRef,
		selectedMicrophone,
		setSelectedMicrophone,
		switchMicrophone,
		audioSettings,
		handleAudioSettingsChange,
		dispose: disposeCapture,
	} = useTranscriberCapture({ roomId, roomType: "online", participantId: captureParticipantId, enabled: appointment.status !== "Completed" });

	const { registerRemoteVideo, getRemoteVideo } = useRemoteVideoRegistry();
	const { noteConnectionState } = useConsultMediaTelemetry({
		enabled: consultationStarted && appointment?.status !== "Completed",
		roomId,
		getSocket: () => (socketServiceRef.current?.getSocket() as any) ?? null,
		getMesh: () => meshRef.current,
		getLocalStream: () => localStreamRef.current,
		getRemoteVideo,
	});
	// The hook re-reads its options every render, so this closure always sees the current transcriberEnabled.
	useCaptureTelemetry({
		enabled: consultationStarted && appointment?.status !== "Completed",
		roomId,
		getSocket: () => (socketServiceRef.current?.getSocket() as any) ?? null,
		getCapture: () => audioStreamingServiceRef.current,
		isDisabled: () => !transcriberEnabled,
	});

	// Route incoming signals into the mesh (utils/consult/peerMesh owns offer/answer/candidate
	// handling, glare and recovery). An inbound offer also means the far side is negotiating with
	// us, so the consult is live — the same signal the old inline handler keyed off.
	const handleVideoSignal = useCallback(
		(data: any) => {
			if (data?.type === "offer") markConsultationStarted();
			return meshRef.current?.handleVideoSignal(data);
		},
		[markConsultationStarted],
	);

	// Handle rejoining an active consultation (e.g. page refresh while session ongoing).
	//
	// Deliberately sends NO offers: joinVideoRoom already made talk fan `offerRequested` to every
	// existing participant naming OUR socket id, so the offers come from them — offering from here as
	// well only manufactures glare. What this owns is consult state and the audio pipeline. When
	// media is not ready yet it defers itself and the init effect re-runs it after getUserMedia; the
	// mesh answers any offer that arrives in that window receive-only and renegotiates to two-way
	// once attachLocalTracksToAll() runs, so the patient is never left without our tracks.
	const handleRejoinActiveConsultation = useCallback(async () => {
		markConsultationStarted();

		if (!mediaReadyRef.current || !localStreamRef.current) {
			pendingReconnectRef.current = true;
			return;
		}
		const wasDeferred = pendingReconnectRef.current;
		pendingReconnectRef.current = false;
		if (wasDeferred) {
			console.log("📹 [Practitioner] Rejoining active consultation");
			message.success("Reconnected to active consultation");
		}

		// Restart audio streaming if needed
		await ensureTranscriberRunning();
	}, [markConsultationStarted, ensureTranscriberRunning]);

	// Init
	useEffect(() => {
		let isComponentMounted = true;
		let roomDataTimeout: ReturnType<typeof setTimeout>;

		// Every event this page registers below, removed again on unmount — EXCEPT `connect` and
		// `disconnect`, which are removed by named reference instead (see below). The list covered
		// 12 of the 20 registrations while claiming to cover them all; the eight it missed included
		// `offerRequested` and `participantJoined`, which run WebRTC peer setup against an unmounted
		// page's refs when the singleton socket is reused for the next consult in the same tab.
		//
		// ⚠️ `connect` AND `disconnect` MUST NEVER BE ADDED HERE. `socket.off(ev)` with no handler
		// argument removes EVERY listener for that event, and SocketService registers its own for
		// both (`socketService.ts::setupEventHandlers`) — the `connect` one's body is
		// `joinRoom(); sendPendingAudioData();`. `sendPendingAudioData` has no other caller, and
		// `sendAudio` queues into `pendingAudioData` whenever the socket is down, so stripping that
		// handler strands every segment captured during a momentary drop in the queue FOREVER:
		// logged as "queued for later" and never sent — a silent, permanent hole in the live
		// transcript and in the saved consultation record.
		const consultSocketEvents = [
			"transcriptUpdate",
			"suggestionUpdate",
			"aiAnalysisUpdated",
			"roomInfo",
			"roomPatientData",
			"roomConsultationData",
			"transcriberStatus",
			"transcriptionError",
			"speakerMapUpdated",
			"videoSignal",
			"videoRoomState",
			"consultationAlreadyActive",
			"patientWaiting",
			"waitingRoomUpdated",
			"patientAdmitted",
			"videoParticipantListUpdate",
			"participantJoined",
			"participantLeft",
			"offerRequested",
			"participantMicSilent",
			"participantRemoved",
			"videoError",
		];

		// Assigned in `init()` below; removed by reference — never by a blanket `off()`.
		let pageConnectHandler: (() => void) | null = null;
		let pageDisconnectHandler: ((reason: string) => void) | null = null;

		async function loadPatientDataFromAPI() {
			if (patientLoadedRef.current) return;
			try {
				const { data: patientData } = await fetchPatient(String(patientId));
				const medHist = await fetchMedicalHistories(patientData.id);
				const tests = await fetchTests(patientData.id);
				const { data: medications } = await fetchMedications(patientData.id);
				if (!isComponentMounted) return;
				setPatient({ ...patientData, medical_history: medHist, tests, medications });
				fetchClinicCompany().then(setCompany).catch(() => { });
				setConsultation((prev) => (prev.app_id ? prev : { ...prev, app_id: appointment.id }));
				if (patientData.patient_info?.notes && typeof patientData.patient_info.notes === "string") {
					setPatientNotes(JSON.parse(patientData.patient_info.notes));
				}
				patientLoadedRef.current = true;
			} catch (e) {
				// Event name + room id ONLY: this block JSON.parses patient_info.notes, and a
				// SyntaxError quotes the fragment it choked on — i.e. the clinical notes.
				console.error("consult.patient.load_failed", { roomId });
			}
		}

		async function init() {
			try {
				const user = getSessionUser() as TeamMember;
				setPractitioner(user);

				if (appointment.status !== "Completed") {
					if (socketServiceRef.current) socketServiceRef.current.disconnect();

					socketServiceRef.current = SocketService.getInstance();
					const socket: any = socketServiceRef.current.initialize(roomId, "online");

					// Remove stale listeners
					consultSocketEvents.forEach((ev) => socket.off(ev));

					// One mesh per room connection. RTCConfiguration (ICE servers included) comes from
					// config inside the mesh — see utils/consult/iceServers.
					meshRef.current?.closeAll();
					meshRef.current = createPeerMesh({
						roomId,
						logTag: "Practitioner",
						// `as any`: socket.io-client v2's `typeof Socket` typing does not expose emit
						// structurally — the same reason every socket in this file is `const socket: any`.
						getSocket: () => (socketServiceRef.current?.getSocket() as any) ?? null,
						getMySocketId: () => mySocketIdRef.current,
						getLocalStream: () => localStreamRef.current,
						onRemoteStream: (peerSocketId, stream) => {
							if (!isComponentMounted) return;
							setRemoteStreams((prev) => new Map(prev).set(peerSocketId, stream));
						},
						onPeerClosed: (peerSocketId) => {
							if (!isComponentMounted) return;
							setRemoteStreams((prev) => {
								const m = new Map(prev);
								m.delete(peerSocketId);
								return m;
							});
						},
						onConnectionStateChange: (peerSocketId, state) => {
							if (!isComponentMounted) return;
							setConnectionState(state as any);
							noteConnectionState(peerSocketId, state);
							const si: any = socketServiceRef.current?.getSocket();
							if (si) si.emit("connectionStateChange", { roomId, connectionState: state });
							if (state === "connected") {
								setReconnecting(false);
							} else if (state === "failed" || state === "disconnected") {
								// The mesh owns the actual recovery (ICE restart, then teardown +
								// requestOffers); this is only the badge in the drawer title.
								setReconnecting(true);
							}
						},
					});

					socket.on("roomPatientData", (data: any) => {
						if (!isComponentMounted) return;
						try {
							const roomPatient = typeof data.patient === "string" ? JSON.parse(data.patient) : data.patient;
							setPatient((prev) => ({ ...prev, ...roomPatient }));
							if (roomPatient.patient_info?.notes && typeof roomPatient.patient_info.notes === "string") {
								setPatientNotes(JSON.parse(roomPatient.patient_info.notes));
							}
							patientLoadedRef.current = true;
							roomDataLoadedRef.current = true;
							previousPatientDataRef.current = getPatientDataSnapshot(roomPatient);
						} catch {
							loadPatientDataFromAPI();
						}
					});

					socket.on("roomConsultationData", (data: any) => {
						if (!isComponentMounted) return;
						try {
							const rc = typeof data.consultationData === "string" ? JSON.parse(data.consultationData) : data.consultationData;
							setConsultation((prev) => ({ ...prev, ...rc }));
						} catch (e) {
							// Event name + room id ONLY. This parses data.consultationData; a
							// SyntaxError quotes the consultation notes it failed on.
							console.error("consult.consultation_data.parse_failed", { roomId });
						}
					});

					socket.on("transcriptUpdate", (data: any) => {
						if (!isComponentMounted) return;
						upsertTranscript(data);
					});

					socket.on("suggestionUpdate", (data: any) => {
						if (!isComponentMounted) return;
						if (data.suggestions && Array.isArray(data.suggestions)) setSuggestions(data.suggestions);
					});

					// talk owns the room's AI flag — it is room state, not tab state, so the two tabs
					// of one consult converge on a single value instead of each keeping its own. It is
					// broadcast back to the emitter too, so a local toggle round-trips and lands here
					// idempotently.
					socket.on("aiAnalysisUpdated", (data: any) => {
						if (!isComponentMounted) return;
						// talk emits `{ roomId, isActive: 'active'|'inactive', timestamp }` — verified
						// against its `setAiAnalysis` handler. Read the one field it sends, not a
						// defensive union: a branch for a field nobody emits reads as a real contract
						// and outlives the reason it was written.
						const active = data?.isActive === "active";
						setAiEnabled(active);
						setAiActive(active);
					});

					// Transcriber health — degradations from talk's audio router, and per-packet
					// transcription failures forwarded from transcribe. Both are safe to register
					// before the server that emits them ships.
					socket.on("transcriberStatus", (data: any) => {
						if (!isComponentMounted) return;
						reportTranscriberStatus(transcriberStatusFromEvent(data));
					});

					socket.on("transcriptionError", (data: any) => {
						if (!isComponentMounted) return;
						reportTranscriberStatus(transcriberStatusFromError(data));
					});

					socket.on("speakerMapUpdated", (data: any) => {
						if (!isComponentMounted) return;
						setSpeakerMap(parseSpeakerMap(data?.speakerMap));
					});

					socket.on("roomInfo", (data: any) => {
						if (!isComponentMounted) return;
						if (roomDataTimeout) clearTimeout(roomDataTimeout);
						let hasData = false;

						if (data.participants) {
							try {
								const p = JSON.parse(data.participants);
								if (Array.isArray(p) && p.length > 0) {
									markConsultationStarted();
									setParticipants(p);
									hasData = true;
								}
							} catch {
								// Event name + room id ONLY, and never the exception: a JSON.parse
								// SyntaxError quotes the fragment it choked on, and these four fields
								// carry the patient record and the consultation notes. Same rule for
								// the three catches below.
								console.error("consult.room_info.participants.parse_failed", { roomId });
							}
						}
						// UNIONED, never assigned: `roomInfo` answers every `joinRoom`, so it arrives
						// again on every reconnect, and talk emits `transcriptUpdate` before it
						// appends to Redis. A segment caught in that window is on screen and absent
						// from the snapshot — replacing the feed would erase it from the record too.
						if (data.transcripts) {
							const t = safeParseTranscripts(data.transcripts);
							if (t.length > 0) {
								markConsultationStarted();
								upsertTranscripts(t);
								hasData = true;
							}
						}
						// Only a snapshot that HAS names replaces the map. talk's Redis-down and its
						// error path both send `speakerMap: '{}'`, which is TRUTHY, so a bare
						// `if (data.speakerMap)` wiped every name on screen on a reconnect during a
						// Redis blip. Clearing a name is `speakerMapUpdated`'s job, and that one
						// still replaces wholesale.
						const roomSpeakerMap = parseSpeakerMap(data.speakerMap);
						if (Object.keys(roomSpeakerMap).length > 0) {
							setSpeakerMap(roomSpeakerMap);
						}
						if (data.suggestions) {
							try {
								const s = JSON.parse(data.suggestions);
								if (Array.isArray(s) && s.length > 0) {
									setSuggestions(s);
									hasData = true;
								}
							} catch {
								console.error("consult.room_info.suggestions.parse_failed", { roomId });
							}
						}
						if (data.patientData) {
							try {
								const rp = JSON.parse(data.patientData);
								setPatient((prev) => ({ ...prev, ...rp }));
								if (rp.patient_info?.notes && typeof rp.patient_info.notes === "string") setPatientNotes(JSON.parse(rp.patient_info.notes));
								patientLoadedRef.current = true;
								roomDataLoadedRef.current = true;
								previousPatientDataRef.current = getPatientDataSnapshot(rp);
								hasData = true;
							} catch {
								console.error("consult.room_info.patient_data.parse_failed", { roomId });
							}
						}
						if (data.consultationData) {
							try {
								const rc = JSON.parse(data.consultationData);
								setConsultation((prev) => ({ ...prev, ...rc }));
								hasData = true;
							} catch {
								console.error("consult.room_info.consultation_data.parse_failed", { roomId });
							}
						}

						if (!hasData || (!data.patientData && !patientLoadedRef.current)) loadPatientDataFromAPI();
						else {
							roomDataLoadedRef.current = true;
						}
						// Populate waiting room from cached room state
						if (data.waitingPatients && Array.isArray(data.waitingPatients)) {
							setWaitingPatients(data.waitingPatients);
						}
					});

					// Waiting room events — received by practitioners
					socket.on("patientWaiting", (data: any) => {
						if (!isComponentMounted) return;
						setWaitingPatients((prev) => {
							const exists = prev.find((p) => p.socketId === data.patient?.socketId);
							if (exists) return prev;
							return [...prev, data.patient];
						});
						notification.info({
							message: "Patient in Waiting Room",
							description: `${data.patient?.displayName || "A patient"} is waiting to join the consultation.`,
							duration: 0, // Keep visible until dismissed
							key: `waiting-${data.patient?.socketId}`,
						});
					});

					socket.on("waitingRoomUpdated", (data: any) => {
						if (!isComponentMounted) return;
						setWaitingPatients(data.waitingPatients || []);
					});

					socket.on("patientAdmitted", (data: any) => {
						if (!isComponentMounted) return;
						setWaitingPatients((prev) => prev.filter((p) => p.socketId !== data.patient?.socketId));
						notification.destroy(`waiting-${data.patient?.socketId}`);
						// Auto-start the consultation when the first patient is admitted
						if (!consultationStartedRef.current) {
							startConsultationFnRef.current();
						}
					});

					socket.on("videoSignal", handleVideoSignal);

					socket.on("videoParticipantListUpdate", (data: any) => {
						if (!isComponentMounted) return;
						if (data.participants && Array.isArray(data.participants)) setVideoParticipants(data.participants);
					});

					// Handle rejoin - unified event listener
					socket.on("videoRoomState", (data: any) => {
						if (!isComponentMounted) return;

						if (data.yourSocketId) {
							mySocketIdRef.current = data.yourSocketId;
							meshRef.current?.resetIfSocketIdChanged();
						}

						if (data.participants && Array.isArray(data.participants)) {
							setVideoParticipants(data.participants);
							const present = new Set(data.participants.map((p: VideoParticipant) => p.socketId));
							setSilentMicPeers((prev) => {
								const kept = new Map(Array.from(prev).filter(([socketId]) => present.has(socketId)));
								return kept.size === prev.size ? prev : kept;
							});
						}

						// If consultation is active, restore consult state + audio; the media rebuild is
						// driven by the offerRequested fan-out talk already sent to the existing peers.
						if (data.consultationActive) {
							handleRejoinActiveConsultation();
						}
					});

					socket.on("consultationAlreadyActive", async (_data: any) => {
						if (!isComponentMounted) return;
						// Handled via videoRoomState
					});

					socket.on("participantJoined", (data: any) => {
						if (data.participants && Array.isArray(data.participants)) setVideoParticipants(data.participants);
						if (data.participantType === "patient") message.info("Patient has joined the consultation");
						else if (data.participantType === "practitioner") message.info("A practitioner has joined the consultation");
					});

					socket.on("participantLeft", (data: any) => {
						const leftSocketId: string = data.socketId;
						message.warning("A participant has left the consultation");
						setVideoParticipants((prev) => prev.filter((p) => p.socketId !== leftSocketId));
						setSilentMicPeers((prev) => withoutKey(prev, leftSocketId));
						settleRemoval(leftSocketId);

						// Close and remove the corresponding peer connection (the mesh also clears its
						// buffered candidates and removes the stream tile via onPeerClosed)
						meshRef.current?.closePeer(leftSocketId);

						// If all remote participants left, reset visual state
						if ((meshRef.current?.peerCount() ?? 0) === 0) setReconnecting(false);
					});

					// talk relays a patient's own silent-mic detection; this page only displays it.
					socket.on("participantMicSilent", (data: any) => {
						if (!isComponentMounted || typeof data?.socketId !== "string") return;
						const { socketId, silent } = data;
						setSilentMicPeers((prev) => (silent === true ? (prev.get(socketId) ? prev : new Map(prev).set(socketId, true)) : withoutKey(prev, socketId)));
					});

					// Staff-wide ack; talk sends participantLeft first, which may already have settled it.
					socket.on("participantRemoved", (data: any) => {
						if (!isComponentMounted) return;
						if (typeof data?.socketId === "string") settleRemoval(data.socketId);
						message.success("Participant removed");
					});

					socket.on("videoError", () => {
						if (!isComponentMounted) return;
						if (settleRemoval()) message.error(REMOVE_FAILED_MESSAGE);
					});

					socket.on("offerRequested", async (data: any) => {
						const { requesterSocketId } = data;
						if (!requesterSocketId) return;
						console.log(`📹 [Practitioner] Received offerRequested from ${requesterSocketId}`);
						await meshRef.current?.handleOfferRequested(requesterSocketId);
					});

					pageConnectHandler = () => {
						console.log("📹 [Practitioner] Socket connected");
						mySocketIdRef.current = socket.id;
						// A reconnect means a NEW socket id: every existing RTCPeerConnection is the
						// stale half of a pair the remote is about to close (participantLeft) and
						// re-offer to the new id, so rebuild from zero rather than reuse.
						meshRef.current?.resetIfSocketIdChanged();

						const displayName = user?.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : "Practitioner";
						const audioParticipantId = `practitioner_${user?.id || "unknown"}`;

						// No joinRoom here: SocketService's connect handler already emits it (socketService.ts:177) — emitting it here too joined every socket twice.
						socket.emit("joinVideoRoom", {
							roomId,
							participantType: "practitioner",
							userId: user?.id || "unknown",
							displayName,
							audioParticipantId,
						});

						if (!roomDataRequestedRef.current) {
							socket.emit("requestRoomData", roomId);
							roomDataRequestedRef.current = true;
							roomDataTimeout = setTimeout(() => {
								if (!roomDataLoadedRef.current && !patientLoadedRef.current) loadPatientDataFromAPI();
							}, 3000);
						}

						if (patientLoadedRef.current && roomDataLoadedRef.current && patient) {
							socket.emit("setPatientData", { roomId, patient, appId: appointment.id });
							previousPatientDataRef.current = getPatientDataSnapshot(patient);
							patientDataSentRef.current = true;
						}

						if (localStreamRef.current) {
							socket.emit("videoReady", { roomId });
						}
					};
					socket.on("connect", pageConnectHandler);

					pageDisconnectHandler = (reason: string) => {
						console.log("📹 [Practitioner] Socket disconnected:", reason);
						if (reason === "transport close" || reason === "transport error") {
							setReconnecting(true);
							message.warning("Connection interrupted. Attempting to reconnect...");
						}
					};
					socket.on("disconnect", pageDisconnectHandler);

					// The timeout keeps the drawer from hanging indefinitely if permissions are denied.
					const stream = await getUserMediaWithTimeout({ video: CAMERA_CONSTRAINTS, audio: MIC_CONSTRAINTS });
					adoptLocalStream(stream);
					if (localVideoRef.current) localVideoRef.current.srcObject = stream;
					setLocalStreamReady(true);

					// Mark media as ready – peer connections are created on-demand per remote participant
					mediaReadyRef.current = true;
					console.log("✓ Got local media stream - media ready");

					// The other half of the refresh race: any peer connection created while getUserMedia
					// was still pending answered receive-only. addTrack here reuses those reserved
					// transceivers and negotiationneeded renegotiates, so the far side gets our A/V —
					// this is the line whose absence produced the one-way-media incident.
					meshRef.current?.attachLocalTracksToAll();

					await enumerateDevices();
					if (!isComponentMounted) return;

					// If reconnection was deferred (e.g. page refresh during active session), trigger it now
					if (pendingReconnectRef.current) {
						console.log("📹 [Practitioner] Triggering deferred reconnection now that media is ready");
						handleRejoinActiveConsultation();
					}
				} else {
					const { data } = await fetchSingleCompletedAppointmentById(appointment.id);
					if (!isComponentMounted) return;
					if (data) {
						// A malformed stored column yields an empty transcript panel, not a thrown
						// init that also loses the consultation record and the suggestions below it.
						setAllTranscripts(data.transcript);
						// A name set from the in-person side of the same room must not vanish here
						// either. This branch opens no socket, so the stored column is the only
						// source — see InPersonConsultation's copy of this line.
						setSpeakerMap(parseSpeakerMap(data.speaker_map));
						setSuggestions(data.suggestions !== "" ? JSON.parse(data.suggestions as string) : []);
						setConsultation(data);
					}
				}
			} catch (e) {
				// Event name + room id ONLY: this block JSON.parses data.suggestions (AI clinical
				// suggestions about this patient). The user-facing message carries nothing.
				console.error("consult.init_failed", { roomId });
				message.error("Failed to initialize consultation");
			}
		}

		init();

		return () => {
			isComponentMounted = false;
			mediaReadyRef.current = false;
			pendingReconnectRef.current = false;
			if (roomDataTimeout) clearTimeout(roomDataTimeout);
			if (patientUpdateTimeoutRef.current) clearTimeout(patientUpdateTimeoutRef.current);
			const socket: any = socketServiceRef.current?.getSocket();
			if (socket && roomId) socket.emit("leaveVideoRoom", { roomId });
			// Symmetric with the registration above. SocketService is a SINGLETON, so a listener
			// left behind here would fire against this unmounted page's setters the next time a
			// consult is opened in the same tab. `connect`/`disconnect` go by named reference so
			// the service's own handlers — one of which flushes queued audio — survive.
			if (socket) {
				consultSocketEvents.forEach((ev) => socket.off(ev));
				if (pageConnectHandler) socket.off("connect", pageConnectHandler);
				if (pageDisconnectHandler) socket.off("disconnect", pageDisconnectHandler);
			}
			settleRemoval();
			socketServiceRef.current?.disconnect();
			socketServiceRef.current = null;
			// Close ALL peer connections in the mesh (buffered candidates and timers go with them)
			meshRef.current?.closeAll();
			meshRef.current = null;
			disposeCapture();
			unwatchLocalTracksRef.current();
			localStreamRef.current?.getTracks().forEach((t) => t.stop());
			localStreamRef.current = null;
			isScreenSharingRef.current = false;
			screenStreamRef.current?.getTracks().forEach((t) => t.stop());
			screenStreamRef.current = null;
			// Close any popped-out video window
			if (pipWindowRef.current && !pipWindowRef.current.closed) pipWindowRef.current.close();
			if (pipIntervalRef.current) {
				clearInterval(pipIntervalRef.current);
				pipIntervalRef.current = null;
			}
		};
		// Every callback here is identity-stable per room (state they need rides in refs), so this
		// effect re-runs only when the consult itself changes — it used to also depend on
		// consultationStarted/selectedMicrophone/transcriberEnabled through its callbacks, which
		// rebuilt the socket, the media and every peer connection mid-consult when any of them flipped.
	}, [roomId, patientId, appointment.id, appointment.status, handleVideoSignal, getPatientDataSnapshot, handleRejoinActiveConsultation, noteConnectionState, settleRemoval]);

	// Keep the room's activity clock alive while the practitioner waits for the patient.
	//
	// talk reaps any room with no inbound socket event for 5 minutes (server.js:1014, :1047). A
	// practitioner who opens the consult ten minutes early — reading notes, not emitting anything —
	// has the room deleted underneath them: `videoRoomStates` is cleared and every socket is
	// `leave()`d. The patient then arrives, `joinWaitingRoom` finds no practitioner in the room, and
	// the `patientWaiting` notification is never sent. Neither side is told anything, because no
	// client listens for `roomClosed`.
	//
	// `heartbeat` is the server's own handler (server.js:2311) and is deliberately ungated — it
	// emits nothing and returns nothing, it only stamps the clock.
	useEffect(() => {
		if (!roomId) return;
		const timer = setInterval(() => {
			const socket: any = socketServiceRef.current?.getSocket();
			if (socket?.connected) socket.emit("heartbeat", roomId);
		}, 60_000);
		return () => clearInterval(timer);
	}, [roomId]);

	// Sync: initial patient data send
	useEffect(() => {
		if (patientLoadedRef.current && !patientDataSentRef.current && !roomDataLoadedRef.current && socketServiceRef.current && patient) {
			const socket: any = socketServiceRef.current.getSocket();
			if (socket?.connected) {
				socket.emit("setPatientData", { roomId, patient, appId: appointment.id });
				previousPatientDataRef.current = getPatientDataSnapshot(patient);
				patientDataSentRef.current = true;
			}
		}
	}, [patient, roomId, appointment.id, getPatientDataSnapshot]);

	useEffect(() => {
		if (roomDataLoadedRef.current && patientLoadedRef.current && !patientDataSentRef.current && patient) {
			previousPatientDataRef.current = getPatientDataSnapshot(patient);
			patientDataSentRef.current = true;
		}
	}, [patient, getPatientDataSnapshot]);

	// Debounced patient data sync
	useEffect(() => {
		if (!patientLoadedRef.current || !patientDataSentRef.current || appointment.status === "Completed" || !patient) return;
		const snap = getPatientDataSnapshot(debouncedPatient);
		if (snap !== previousPatientDataRef.current) {
			const si: any = socketServiceRef.current?.getSocket();
			if (si?.connected) {
				si.emit("updatePatientData", { roomId, patient: debouncedPatient, changeType: "incremental_update", timestamp: Date.now() });
				previousPatientDataRef.current = snap;
				message.success("Patient data updated for AI suggestions", 2);
			}
		}
	}, [debouncedPatient, roomId, getPatientDataSnapshot, appointment.status]);

	// Consultation notes/vitals sync
	useEffect(() => {
		const si: any = socketServiceRef.current?.getSocket();
		if (si && consultation) {
			si.emit("setConsultationData", {
				roomId,
				consultationNotes: consultation.notes || "",
				consultParticulars: consultation.vitals || "",
				appId: appointment.id,
			});
		}
	}, [consultation.notes, consultation.vitals, roomId, appointment.id]);

	// Participant sync
	useEffect(() => {
		const si: any = socketServiceRef.current?.getSocket();
		if (si) {
			si.emit("setRoomParticipants", {
				roomId,
				participantCount: participants.length,
				participants: JSON.stringify(participants),
				aiActive: aiEnabled ? "active" : "inactive",
				transcriberActive: transcriberEnabled,
			});
		}
	}, [participants, roomId, transcriberEnabled, aiEnabled]);

	// Suggestion update — UNCHANGED. Accept/dismiss is still talk's write; only the generation moved.
	const updateSuggestionStatus = useCallback(
		(suggestionId: string, updates: Partial<Suggestion>) => {
			const si: any = socketServiceRef.current?.getSocket();
			if (si) si.emit("updateSuggestion", { roomId, suggestionId, updates });
		},
		[roomId],
	);

	// ── AI suggestions ────────────────────────────────────────────────────────────────────────
	// talk holds no AI at all any more. This tab runs the pass itself — three generation calls
	// through healthcare's consult-generation relay — and pushes the merged set BACK to talk, which
	// stays the room's STORE and ROUTER: it whole-replaces `roomInfo.suggestions` and broadcasts
	// `suggestionUpdate` to every tab, this one included. The panel therefore still renders from the
	// socket, exactly as it did when talk generated.
	const { runRound: runSuggestionRound } = useConsultSuggestions({
		roomId,
		patient,
		transcripts,
		suggestions,
		consultationNotes: consultation.notes || "",
		consultParticulars: consultation.vitals || "",
		// A completed record loads its whole transcript on open, which is indistinguishable from a
		// live consult that has just said a great deal — and it must never generate.
		enabled: aiEnabled && appointment.status !== "Completed",
	});

	// Driven off the transcript feed: `transcripts` changes only when talk delivers a
	// `transcriptUpdate`, or when the room snapshot lands on join. Read from an effect rather than
	// from inside the socket handler so the pass sees the segment that triggered it — the handler
	// runs before React has committed the new state, so a handler-side call would always be one
	// segment behind.
	//
	// The 12s triage cooldown, the 3-segment minimum and the in-flight drop all live in the hook,
	// so this may fire as often as the room speaks.
	useEffect(() => {
		runSuggestionRound().then((round) => {
			// No cancel-on-cleanup here, deliberately: the emit's target is the ROOM, not this
			// component's state, and a round that finishes after the next segment has arrived still
			// holds real suggestions. Dropping it would lose them. An unmounted page has no socket,
			// so the emit is a no-op by then.
			if (!round?.hasChanges) return;

			const si: any = socketServiceRef.current?.getSocket();
			si?.emit("setSuggestions", { roomId, suggestions: round.suggestions });
		});
	}, [transcripts, runSuggestionRound, roomId]);

	// Re-attach local stream to the video element whenever the element may have
	// mounted or re-mounted (e.g. Drawer first opens, remote stream count changes,
	// or the video is popped out/in to a separate window).
	useEffect(() => {
		if (localVideoRef.current && localStreamRef.current) {
			localVideoRef.current.srcObject = localStreamRef.current;
			localVideoRef.current.play().catch(() => { });
		}
	}, [localStreamReady, remoteStreams, videoPopped]);

	// Toggle the video stream in/out of a separate popup window
	const togglePopOut = useCallback(() => {
		if (videoPopped) {
			if (pipWindowRef.current && !pipWindowRef.current.closed) pipWindowRef.current.close();
			if (pipIntervalRef.current) {
				clearInterval(pipIntervalRef.current);
				pipIntervalRef.current = null;
			}
			pipWindowRef.current = null;
			setPipContainer(null);
			setVideoPopped(false);
			return;
		}
		const pipWin = window.open("", "VideoConsultationPopout", "width=720,height=540,top=100,left=100,toolbar=0,menubar=0,location=0,status=0,resizable=1");
		if (!pipWin) {
			message.error("Popup blocked — please allow popups for this site to use video pop-out.");
			return;
		}
		pipWin.document.title = "Video Consultation";
		pipWin.document.documentElement.style.cssText = "width:100%;height:100%;overflow:hidden;";
		pipWin.document.body.style.cssText = "margin:0;padding:0;background:#000;height:100vh;overflow:hidden;";
		const container = pipWin.document.createElement("div");
		container.style.cssText = "width:100%;height:100%;position:relative;";
		pipWin.document.body.appendChild(container);
		// Inject a script into the popup that continuously force-plays any paused
		// video elements. This is needed because React effects fire asynchronously
		// (after user-activation context has expired for the popup), so unmuted
		// remote streams would otherwise be blocked by the browser's autoplay policy.
		const autoPlayScript = pipWin.document.createElement("script");
		autoPlayScript.textContent = [
			"(function(){",
			"  function playAll(){",
			"    document.querySelectorAll('video').forEach(function(v){",
			"      if(v.srcObject && v.paused && !v.ended){ v.play().catch(function(){}); }",
			"    });",
			"  }",
			"  var obs = new MutationObserver(playAll);",
			"  obs.observe(document.body,{childList:true,subtree:true});",
			"  setInterval(playAll,300);",
			"})()",
		].join("\n");
		pipWin.document.head.appendChild(autoPlayScript);
		pipWindowRef.current = pipWin;
		setPipContainer(container);
		setVideoPopped(true);
		// Poll so we detect when the user closes the popup window manually
		pipIntervalRef.current = setInterval(() => {
			if (pipWin.closed) {
				clearInterval(pipIntervalRef.current!);
				pipIntervalRef.current = null;
				setPipContainer(null);
				setVideoPopped(false);
				pipWindowRef.current = null;
			}
		}, 500);
	}, [videoPopped]);

	const startConsultation = useCallback(async () => {
		try {
			const socket: any = socketServiceRef.current?.getSocket();
			if (socket) {
				socket.emit("videoReady", { roomId });
				// Signal to every existing peer that the consultation is now active.
				// In the mesh model peer connections are already established via offerRequested;
				// we just broadcast the "consultationStarted" event for UI state.
				socket.emit("startConsultation", roomId);
			}

			await startCapture();

			setAiActive(aiEnabled);
			message.success("Consultation started");
		} catch (e) {
			console.error("Start error:", e);
			message.error("Failed to start consultation");
		}
	}, [aiEnabled, roomId, startCapture]);

	// Keep ref in sync so socket listeners can call the latest startConsultation without stale closures
	useEffect(() => {
		startConsultationFnRef.current = startConsultation;
	}, [startConsultation]);

	// Screen share – replaces the video track in every active peer connection
	const stopScreenShare = useCallback(() => {
		const screenStream = screenStreamRef.current;
		// Also reached from the track's ended event, which can follow our own stop.
		if (!isScreenSharingRef.current || !screenStream) return;
		isScreenSharingRef.current = false;
		screenStreamRef.current = null;

		const socket: any = socketServiceRef.current?.getSocket();
		if (socket) socket.emit("screenShareStopped", { roomId });
		const camTrack = localStreamRef.current?.getVideoTracks()[0];
		if (camTrack) {
			meshRef.current?.forEachPc((pc) => {
				const vs = pc.getSenders().find((s) => s.track?.kind === "video");
				if (vs) vs.replaceTrack(camTrack);
			});
		}
		if (localVideoRef.current && localStreamRef.current) localVideoRef.current.srcObject = localStreamRef.current;
		screenStream.getTracks().forEach((t) => t.stop());
		setIsScreenSharing(false);
		message.success("Screen sharing stopped");
	}, [roomId]);

	const startScreenShare = useCallback(async () => {
		try {
			const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
			const screenTrack = screenStream.getVideoTracks()[0];

			meshRef.current?.forEachPc((pc) => {
				const vs = pc.getSenders().find((s) => s.track?.kind === "video");
				if (vs && screenTrack) vs.replaceTrack(screenTrack);
			});

			screenStreamRef.current = screenStream;
			isScreenSharingRef.current = true;
			if (localVideoRef.current) localVideoRef.current.srcObject = screenStream;
			const socket: any = socketServiceRef.current?.getSocket();
			if (socket) socket.emit("screenShareStarted", { roomId });
			// The browser's own "Stop sharing" bar; stopScreenShare reads refs, so this cannot go stale.
			screenTrack.onended = stopScreenShare;
			setIsScreenSharing(true);
			message.success("Screen sharing started");
		} catch {
			message.error("Failed to start screen sharing");
		}
	}, [roomId, stopScreenShare]);

	const toggleScreenShare = useCallback(() => {
		if (isScreenSharingRef.current) stopScreenShare();
		else startScreenShare();
	}, [startScreenShare, stopScreenShare]);

	// A still-listed selection is kept; `reseed` kinds follow the device actually in use.
	const enumerateDevices = async (reseed: MediaKind[] = []) => {
		try {
			const { cameras: videoDevices, microphones: audioDevices } = await listInputDevices();
			setCameras(videoDevices);
			setMicrophones(audioDevices);
			const stream = localStreamRef.current;
			if (videoDevices.length > 0) setSelectedCamera((prev) => seedDeviceSelection(videoDevices, stream?.getVideoTracks()[0], reseed.includes("video") ? undefined : prev));
			if (audioDevices.length > 0) setSelectedMicrophone((prev) => seedDeviceSelection(audioDevices, stream?.getAudioTracks()[0], reseed.includes("audio") ? undefined : prev));
		} catch (err) {
			console.warn("consult.devices.enumerate_failed", { errorName: (err as { name?: string })?.name ?? "unknown" });
		}
	};

	// devicechange can fire when another application takes a device, so this only refreshes the list — it never switches.
	useEffect(() => {
		if (!localStreamReady) return;
		return watchDeviceList(() => {
			enumerateDevices();
		});
	}, [localStreamReady]);

	const adoptLocalStream = (stream: MediaStream) => {
		localStreamRef.current = stream;
		unwatchLocalTracksRef.current();
		unwatchLocalTracksRef.current = watchTrackEnded(stream, handleLocalTrackEnded);
	};

	// Swap first, stop last: a peer is never left sending an ended track. While sharing, the senders and preview keep the screen; stopScreenShare sends this camera.
	const commitReplacement = async (oldStream: MediaStream, { stream }: Replacement) => {
		adoptLocalStream(stream);
		if (!isScreenSharingRef.current && localVideoRef.current) localVideoRef.current.srcObject = stream;
		const swapped = await swapOntoPeers((visit) => meshRef.current?.forEachPc(visit), stream, { skipVideo: isScreenSharingRef.current });
		if (!swapped) console.warn("consult.device_change.replace_failed");
		releaseReplaced(oldStream, stream);
	};

	const handleLocalTrackEnded = (track: MediaStreamTrack) => {
		// Our own stop() (a swap, consult end, unmount) has already dropped the track from the live stream.
		if (!localStreamRef.current?.getTracks().includes(track)) return;
		console.warn("consult.device_lost", { kind: track.kind });
		recoverLostDevices();
	};

	const recoverLostDevices = async () => {
		if (recoveringDeviceRef.current) {
			recheckLostDeviceRef.current = true;
			return;
		}
		recoveringDeviceRef.current = true;
		try {
			do {
				recheckLostDeviceRef.current = false;
				await fallBackToDefaultDevices();
			} while (recheckLostDeviceRef.current && localStreamRef.current);
		} finally {
			recoveringDeviceRef.current = false;
		}
	};

	const fallBackToDefaultDevices = async () => {
		const oldStream = localStreamRef.current;
		const lost = (oldStream?.getTracks() ?? []).filter((track) => track.readyState === "ended").map((track) => track.kind as MediaKind);
		if (!oldStream || lost.length === 0) return;
		const device = lost.length > 1 ? "camera and microphone" : lost[0] === "audio" ? "microphone" : "camera";
		const was = lost.length > 1 ? "were" : "was";

		let replacement: Replacement | null;
		try {
			replacement = await acquireReplacement({
				current: oldStream,
				cameraId: lost.includes("video") ? null : undefined,
				micId: lost.includes("audio") ? null : undefined,
				videoConstraints: CAMERA_CONSTRAINTS,
				audioConstraints: MIC_CONSTRAINTS,
			});
		} catch (err) {
			if (localStreamRef.current !== oldStream) return;
			console.warn("consult.device_lost.fallback_failed", { errorName: (err as { name?: string })?.name ?? "unknown" });
			message.error(`Your ${device} ${was} disconnected and the default could not be opened. Open Settings to choose another device.`, 8);
			return;
		}
		if (!replacement) return;
		// Unmounted, room changed or an Apply won while the default was opening.
		if (localStreamRef.current !== oldStream) {
			releaseReplaced(replacement.stream, oldStream);
			return;
		}

		await commitReplacement(oldStream, replacement);
		message.warning(`Your ${device} ${was} disconnected — switched to your default ${device}`);
		// The transcription capture was on the same lost microphone.
		if (replacement.changedAudio && localStreamRef.current === replacement.stream) await switchMicrophone(replacement.stream.getAudioTracks()[0]?.getSettings().deviceId ?? "");
		enumerateDevices(lost);
	};

	// Acquire-then-swap: a failed getUserMedia leaves the current camera and mic live on every peer and in transcription.
	const applyDevices = async (cameraId: string, micId: string): Promise<boolean> => {
		if (switchingDevicesRef.current) return false;
		switchingDevicesRef.current = true;
		try {
			return await switchDevices(cameraId, micId);
		} finally {
			switchingDevicesRef.current = false;
		}
	};

	const switchDevices = async (cameraId: string, micId: string): Promise<boolean> => {
		const oldStream = localStreamRef.current;
		if (!oldStream) {
			message.error("Failed to switch camera or microphone");
			return false;
		}
		const micChanged = micId !== selectedMicrophone;
		let replacement: Replacement | null;
		try {
			replacement = await acquireReplacement({
				current: oldStream,
				cameraId: cameraId !== selectedCamera ? cameraId : undefined,
				micId: micChanged ? micId : undefined,
				videoConstraints: CAMERA_CONSTRAINTS,
				audioConstraints: MIC_CONSTRAINTS,
			});
		} catch (err) {
			console.warn("consult.device_change.failed", { errorName: (err as { name?: string })?.name ?? "unknown" });
			if (localStreamRef.current === oldStream) message.error(isGetUserMediaTimeout(err) ? "Your device didn't respond — try again or pick another" : "Failed to switch camera or microphone");
			return false;
		}
		if (replacement) {
			// Unmounted or the room changed while the device was opening.
			if (localStreamRef.current !== oldStream) {
				releaseReplaced(replacement.stream, oldStream);
				return false;
			}
			await commitReplacement(oldStream, replacement);
			if (localStreamRef.current !== replacement.stream) return true;
		}
		setSelectedCamera(cameraId);
		if (micChanged && !(await switchMicrophone(micId))) message.warning("Microphone switched, but transcription could not open it");
		return true;
	};

	// Stop consultation
	const stopConsultation = useCallback(async () => {
		try {
			stopScreenShare();
			stopCapture();
			unwatchLocalTracksRef.current();
			localStreamRef.current?.getTracks().forEach((track) => track.stop());

			meshRef.current?.closeAll();
			setRemoteStreams(new Map());

			const socket: any = socketServiceRef.current?.getSocket();
			if (socket) {
				socket.emit("endConsultation", roomId);
			}

			setAiActive(false);

			// Always show the end modal for whoever stopped the consultation — only
			// the person who clicks Stop triggers this path. Secondary practitioners
			// receive the socket 'endConsultation' event instead.
			setIsEndModalOpen(true);

			message.success("Consultation ended");
		} catch (e) {
			console.error("Stop consultation error:", e);
			message.error("Failed to end consultation");
		}
	}, [roomId, appointment.practitioner_id, handleCloseConsultationModal, stopCapture, stopScreenShare]);

	// Toggle AI
	//
	// `POST /api/toggle-ai-analysis` is DELETED — talk generates nothing, so it has no analysis to
	// arm. The flag is still ROOM state (both tabs must agree, and it has to survive a refresh
	// through `roomInfo`), so it goes over the socket instead: talk writes `roomInfo.aiAnalysis` and
	// broadcasts `aiAnalysisUpdated` back to the room, which the handler in the init effect folds
	// in. Leaving the dead HTTP call in place would 404 in silence and never arm the analysis.
	const toggleAI = useCallback(() => {
		const newAiEnabled = !aiEnabled;
		setAiEnabled(newAiEnabled);
		setAiActive(newAiEnabled);

		const si: any = socketServiceRef.current?.getSocket();
		si?.emit("setAiAnalysis", {
			roomId,
			isActive: newAiEnabled ? "active" : "inactive",
		});
	}, [aiEnabled, roomId]);

	const updateConsultVitals = useCallback((data: string) => {
		setConsultation((prev) => ({ ...prev, vitals: data }));
	}, []);

	useEffect(() => {
		if (patient) setPatient((prev) => ({ ...prev, patient_info: { ...prev.patient_info, notes: JSON.stringify(patientNotes) } }));
	}, [patientNotes]);

	const handleSavePatientNotes = async () => {
		if (patient) {
			await updatePatient(patient.id, patient);
			message.success("Notes saved successfully");
		}
	};

	const { endConsult } = useEndConsult({ appointment, patient, roomId });

	const admitWaitingPatient = useCallback(
		(patientSocketId: string) => {
			const socket: any = socketServiceRef.current?.getSocket();
			socket?.emit("admitPatient", { roomId, patientSocketId });
		},
		[roomId],
	);

	// talk tells only the patient (waitingRoomDismissed), never staff, so the row is dropped here; a reconnect's roomInfo restores the truth.
	const dismissWaitingPatient = useCallback(
		(patientSocketId: string) => {
			const socket: any = socketServiceRef.current?.getSocket();
			if (!socket) return;
			socket.emit("dismissWaitingPatient", { roomId, patientSocketId, reason: DISMISS_REASON });
			setWaitingPatients((prev) => prev.filter((p) => p.socketId !== patientSocketId));
			notification.destroy(`waiting-${patientSocketId}`);
		},
		[roomId],
	);

	const removeParticipant = useCallback(
		(targetSocketId: string) => {
			const socket: any = socketServiceRef.current?.getSocket();
			if (!socket || removalTimersRef.current.has(targetSocketId)) return;
			socket.emit("removeParticipant", { roomId, targetSocketId });
			removalTimersRef.current.set(
				targetSocketId,
				setTimeout(() => {
					if (settleRemoval(targetSocketId)) message.error(REMOVE_FAILED_MESSAGE);
				}, REMOVE_ACK_TIMEOUT_MS),
			);
			setPendingRemovals(new Set(removalTimersRef.current.keys()));
		},
		[roomId, settleRemoval],
	);

	if (!patient || !practitioner) return null;

	const readOnly = appointment.status === "Completed";

	// One object feeds both TranscriptList render sites (right panel and side drawer), so they cannot drift.
	const transcriptProps: TranscriptListProps = {
		patient,
		practitioner,
		transcripts,
		toggleTranscriber: toggleTranscribeListener,
		transcriberActive,
		isDisabled: readOnly,
		audioStreamingServiceRef,
		displayNames,
		speakerMap,
		transcriberStatus,
		transcriptStatus: consultation.transcript_status,
	};

	// Video-only toolbar buttons; the shared title slots them between Profile and Settings.
	const titleActions = (
		<>
			<Popover
				open={participantsPopoverOpen}
				onOpenChange={setParticipantsPopoverOpen}
				content={
					<ParticipantListPanel
						videoParticipants={videoParticipants}
						waitingPatients={waitingPatients}
						mySocketId={mySocketIdRef.current}
						consultationStarted={consultationStarted}
						pendingRemovals={pendingRemovals}
						onAdmit={admitWaitingPatient}
						onDismiss={dismissWaitingPatient}
						onRemove={removeParticipant}
					/>
				}
				title="Participants"
				trigger="click"
				placement="bottomRight"
			>
				<Tooltip title="Participants">
					<Badge count={videoParticipants.length} size="small" offset={[-2, 2]} style={{ backgroundColor: videoParticipants.some((p) => p.participantType.toLowerCase() === "patient") ? "#52c41a" : "#faad14" }}>
						<Button type="text" size="small" icon={<TeamOutlined />} />
					</Badge>
				</Tooltip>
			</Popover>
			<Tooltip title="Screen Share">
				<Button type="text" size="small" icon={<DesktopOutlined style={{ color: isScreenSharing ? "#52c41a" : undefined }} />} onClick={toggleScreenShare} disabled={!consultationStarted} />
			</Tooltip>
		</>
	);

	const drawerTitle = (
		<ConsultDrawerTitle
			patient={patient}
			appointment={appointment}
			captureState={captureState}
			reconnecting={reconnecting}
			consultationStarted={consultationStarted}
			onStart={startConsultation}
			onStop={stopConsultation}
			transcriptCount={transcripts.length}
			onOpenTranscript={() => setTranscriptDrawerOpen(true)}
			onOpenPatientProfile={() => setIsPatientModalOpen(true)}
			onOpenSettings={() => setAdvancedSettingsVisible(true)}
			readOnly={readOnly}
			actions={titleActions}
		/>
	);

	// Multi-participant video grid — video-only, so it rides the shell's rightTop slot above the shared panel.
	const videoGrid = (
		<div
			style={{
				height: videoExpanded ? "60%" : "35%",
				minHeight: 180,
				position: "relative",
				background: "#000",
				transition: "height 0.3s ease",
				overflow: "hidden",
				flexShrink: 0,
			}}
		>
			{/* Out-specify global.css's !important .ant-btn-text overrides, which strip the dark backing over video. */}
			<style>{`
				.ant-btn.ant-btn-text.consult-video-overlay-btn,
				.ant-btn.ant-btn-text.consult-video-overlay-btn:hover,
				.ant-btn.ant-btn-text.consult-video-overlay-btn:focus-visible {
					background: rgba(0,0,0,0.5) !important;
					color: var(--mg-text-on-primary) !important;
				}
				.ant-btn.ant-btn-default.consult-video-overlay-btn,
				.ant-btn.ant-btn-default.consult-video-overlay-btn:hover,
				.ant-btn.ant-btn-default.consult-video-overlay-btn:focus-visible {
					background: rgba(255,255,255,0.15) !important;
					border-color: rgba(255,255,255,0.3) !important;
					color: var(--mg-text-on-primary) !important;
				}
			`}</style>
			{reconnecting && (
				<div
					style={{
						position: "absolute",
						inset: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(0,0,0,0.5)",
						zIndex: 10,
					}}
				>
					<Space>
						<SyncOutlined spin style={{ fontSize: 20 }} />
						<Text style={{}}>Reconnecting video...</Text>
					</Space>
				</div>
			)}

			{/* Dynamic grid of remote participant streams */}
			{videoPopped ? (
				<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(255,255,255,0.5)", gap: 12 }}>
					<ExportOutlined style={{ fontSize: 32 }} />
					<Text style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>Video is open in a separate window</Text>
					<Button size="small" onClick={togglePopOut} className="consult-video-overlay-btn">
						Pop back in
					</Button>
				</div>
			) : (
				<>
					{(() => {
						const entries = Array.from(remoteStreams.entries());
						const totalRemote = entries.length;
						const cols = totalRemote === 0 ? 1 : totalRemote === 1 ? 1 : totalRemote <= 4 ? 2 : 3;
						return (
							<div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 2, width: "100%", height: "100%" }}>
								{totalRemote === 0 ? (
									<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
										<VideoCameraOutlined style={{ fontSize: 32, marginBottom: 8 }} />
										<Text style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>{consultationStarted ? "Waiting for other participants..." : "Start consultation to connect video"}</Text>
									</div>
								) : (
									entries.map(([socketId, stream]) => {
										const peer = videoParticipants.find((p) => p.socketId === socketId);
										const label = peer?.displayName || (peer?.participantType ? peer.participantType.charAt(0).toUpperCase() + peer.participantType.slice(1) : "Participant");
										return <RemoteVideoTile key={socketId} socketId={socketId} stream={stream} label={label} isScreenShare={isScreenSharing} micSilent={silentMicPeers.has(socketId)} registerVideo={registerRemoteVideo} />;
									})
								)}
							</div>
						);
					})()}

					{/* Local video – small overlay */}
					<video
						ref={localVideoCallbackRef}
						autoPlay
						muted
						playsInline
						style={{
							position: "absolute",
							bottom: 10,
							right: 10,
							width: remoteStreams.size === 0 ? "100%" : 120,
							height: remoteStreams.size === 0 ? "100%" : 80,
							border: remoteStreams.size > 0 ? "2px solid rgba(255,255,255,0.3)" : "none",
							borderRadius: remoteStreams.size > 0 ? 8 : 0,
							boxShadow: remoteStreams.size > 0 ? "0 2px 8px rgba(0,0,0,0.4)" : "none",
							objectFit: "cover",
							zIndex: 2,
							transition: "all 0.3s ease",
						}}
					/>

					{isScreenSharing && (
						<div style={{ position: "absolute", top: 10, right: 10, padding: "4px 12px", background: "var(--primary-color)", borderRadius: 6, zIndex: 5 }}>
							<Space size={4}>
								<DesktopOutlined style={{ fontSize: 12 }} />
								<Text style={{ fontSize: 12 }}>Sharing Screen</Text>
							</Space>
						</div>
					)}
				</>
			)}

			{/* Pop-out button */}
			<Tooltip title={videoPopped ? "Pop back in" : "Pop out to separate window"}>
				<Button
					type="text"
					size="small"
					icon={<ExportOutlined style={{ color: videoPopped ? "#faad14" : "#fff" }} />}
					onClick={togglePopOut}
					className="consult-video-overlay-btn"
					style={{ position: "absolute", top: 10, left: 46, zIndex: 5 }}
				/>
			</Tooltip>

			<Tooltip title={videoExpanded ? "Minimize" : "Expand"}>
				<Button
					type="text"
					size="small"
					icon={videoExpanded ? <CompressOutlined style={{}} /> : <ExpandOutlined style={{}} />}
					onClick={() => setVideoExpanded((v) => !v)}
					className="consult-video-overlay-btn"
					style={{ position: "absolute", top: 10, left: 10, zIndex: 5 }}
				/>
			</Tooltip>
		</div>
	);

	return (
		<>
			<ConsultDrawerShell
				title={drawerTitle}
				open={isModalOpen}
				onClose={handleCloseConsultationModal}
				captureState={captureState}
				onResumeCapture={toggleTranscribeListener}
				left={
					<ConsultContentTabs
						appointment={appointment}
						patient={patient}
						setPatient={setPatient}
						consultation={consultation}
						onVitalsChange={updateConsultVitals}
						readOnly={readOnly}
						consultSessionId={consultSessionId}
						activePlan={activePlan}
						onActivePlanClose={() => setActivePlan(null)}
						onPlanChange={reloadPlan}
						company={company}
						handleAutoSave={handleAutoSave}
					/>
				}
				rightTop={videoGrid}
				right={
					<ConsultRightPanel
						mode={rightPanelMode}
						onModeChange={setRightPanelMode}
						activePlan={activePlan}
						consultSessionId={consultSessionId}
						patient={patient}
						setPatient={setPatient}
						practitioner={practitioner}
						appointment={appointment}
						isDisabled={readOnly}
						suggestions={{ items: suggestions, aiActive, toggleAI, updateSuggestion: updateSuggestionStatus }}
						notes={{ items: patientNotes, setItems: setPatientNotes, onSave: handleSavePatientNotes }}
						transcript={transcriptProps}
						chat={{
							messages: chatMessages,
							onSend: sendChatMessage,
							onAttach: sendChatAttachment,
							unreadCount: chatUnreadCount,
							error: chatError,
							onDismissError: dismissChatError,
							emptyText: "No messages yet. Send the patient a note or a file without leaving the consult.",
						}}
					/>
				}
			/>

			<ConsultTranscriptDrawer open={transcriptDrawerOpen} onClose={() => setTranscriptDrawerOpen(false)} count={transcripts.length} transcript={transcriptProps} />

			{/* Other Modals */}
			{advancedSettingsVisible && (
				<AdvancedSettingsDialog
					visible={advancedSettingsVisible}
					onHide={() => setAdvancedSettingsVisible(false)}
					cameras={cameras}
					selectedCamera={selectedCamera}
					microphones={microphones}
					selectedMicrophone={selectedMicrophone}
					onDevicesApply={applyDevices}
					voiceThreshold={audioSettings.voiceThreshold}
					silenceTimeout={audioSettings.silenceTimeout}
					noiseSuppression={audioSettings.noiseSuppression}
					echoCancellation={audioSettings.echoCancellation}
					autoGainControl={audioSettings.autoGainControl}
					onSettingsChange={handleAudioSettingsChange}
				/>
			)}

			{isEndModalOpen && <EndConsultModal isOpen={isEndModalOpen} closeModal={() => {setIsEndModalOpen(false); handleCloseConsultationModal();}} onFinalise={endConsult} patient={patient} setPatient={setPatient} suggestions={suggestions} transcript={transcripts} appointment={appointment} consult={consultation} speakerMap={speakerMap} roomId={roomId} chat={chatMessages} />}

			{isPatientModalOpen && <EditPatientModal patient={patient} isOpen={isPatientModalOpen} onClose={() => setIsPatientModalOpen(false)} />}

			{/* Portal: render live video grid into detached popup window */}
			{pipContainer &&
				createPortal(
					<div style={{ width: "100%", height: "100%", position: "relative", background: "#000", overflow: "hidden" }}>
						{(() => {
							const entries = Array.from(remoteStreams.entries());
							const totalRemote = entries.length;
							const cols = totalRemote <= 1 ? 1 : totalRemote <= 4 ? 2 : 3;
							return (
								<div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 2, width: "100%", height: "100%" }}>
									{totalRemote === 0 ? (
										<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(255,255,255,0.4)", fontFamily: "sans-serif" }}>
											<span style={{ fontSize: 14 }}>Waiting for participants...</span>
										</div>
									) : (
										entries.map(([socketId, stream]) => {
											const peer = videoParticipants.find((p) => p.socketId === socketId);
											const label = peer?.displayName || (peer?.participantType ? peer.participantType.charAt(0).toUpperCase() + peer.participantType.slice(1) : "Participant");
											return <RemoteVideoTile key={socketId} socketId={socketId} stream={stream} label={label} isScreenShare={isScreenSharing} micSilent={silentMicPeers.has(socketId)} registerVideo={registerRemoteVideo} />;
										})
									)}
								</div>
							);
						})()}
						<video
							ref={localVideoCallbackRef}
							autoPlay
							muted
							playsInline
							style={{
								position: "absolute",
								bottom: 10,
								right: 10,
								width: remoteStreams.size === 0 ? "100%" : 150,
								height: remoteStreams.size === 0 ? "100%" : 100,
								objectFit: "cover",
								border: remoteStreams.size > 0 ? "2px solid rgba(255,255,255,0.3)" : "none",
								borderRadius: remoteStreams.size > 0 ? 8 : 0,
								zIndex: 2,
							}}
						/>
					</div>,
					pipContainer,
				)}
		</>
	);
};

export default VideoConsultation;
